// src/components/Footer.tsx
import { Facebook, Instagram, Twitter, Heart, Phone, Mail, MapPin, LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';

type FooterItem = {
  label: string;
  href?: string;
  icon?: LucideIcon;
};

type PolicyLink = {
  label: string;
  href: string;
};

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const footerSections: { title: string; items: FooterItem[] }[] = [
    {
      title: 'DermaKlinika',
      items: [
        {
          label: 'Trajno odstranjevanje dlak z medicinskim diodnim laserjem za gladko kožo.',
        },
        { label: 'Facebook', href: 'https://facebook.com/dermaklinikamaribor', icon: Facebook },
        { label: 'Instagram', href: 'https://instagram.com/dermaklinikamaribor', icon: Instagram },
        { label: 'Twitter', href: 'https://twitter.com/dermaklinika', icon: Twitter },
      ],
    },
    {
      title: 'Hitre Povezave',
      items: [
        { label: 'Domov', href: '/' },
        { label: 'O Laserskem Odstranjevanju', href: '/about-laser' },
        { label: 'Cene in Akcije', href: '/pricing' },
        { label: 'Kontakt', href: '/contact' },
        { label: 'Pogosta Vprašanja', href: '/faq' },
      ],
    },
    {
      title: 'Kontakt',
      items: [
        { label: 'Maribor, Slovenija', icon: MapPin },
        { label: '070 691 750', href: 'tel:070691750', icon: Phone },
        { label: 'dermaklinikamaribor@gmail.com', href: 'mailto:dermaklinikamaribor@gmail.com', icon: Mail },
      ],
    },
  ];

  const policyLinks: PolicyLink[] = [
    { label: 'Politika Zasebnosti', href: '/privacy' },
    { label: 'Pogoji Uporabe', href: '/terms' },
  ];

  return (
    <footer className="bg-nail-800 text-white pt-12 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {footerSections.map((section, index) => (
            <Card
              key={index}
              className="bg-nail-700 border-nail-600 rounded-2xl footer-card-glow"
            >
              <CardContent className="p-6">
                <h4 className="text-lg font-medium mb-4 text-gold-500">{section.title}</h4>
                {section.title === 'DermaKlinika' ? (
                  <div className="mb-4">
                    <Link
                      to="/"
                      className="text-2xl font-serif font-semibold text-white hover:text-gold-500 transition-colors"
                      onClick={scrollToTop}
                    >
                      Derma<span className="text-gold-500">Klinika</span>
                    </Link>
                  </div>
                ) : null}
                <ul className="space-y-2">
                  {section.items.map((item, idx) => (
                    <li key={idx}>
                      {item.href && item.icon ? (
                        <a
                          href={item.href}
                          className="text-nail-100 hover:text-white/90 hover:underline transition-colors flex items-center gap-2"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <item.icon size={18} />
                          {item.label}
                        </a>
                      ) : item.href ? (
                        <Link
                          to={item.href}
                          className="text-nail-100 hover:text-white/90 hover:underline transition-colors"
                          onClick={scrollToTop}
                        >
                          {item.label}
                        </Link>
                      ) : (
                        <span className="text-nail-100">{item.label}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="border-t border-nail-700 pt-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-nail-300 text-sm text-center md:text-left">
              © {new Date().getFullYear()} Derma Klinika Maribor. Vse pravice pridržane.
            </p>
            <p className="text-nail-300 text-sm text-center">
              Izdelano z <Heart size={16} className="inline text-nail-300 animate-pulse" /> by{' '}
              <a
                href="https://digitalpro.dev/"
                className="text-nail-100 hover:text-white/90 hover:underline transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                DigitalPro
              </a>
            </p>
            <div className="flex flex-col md:flex-row gap-3 md:gap-6 text-center md:text-right">
              {policyLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className="text-nail-300 hover:text-white/90 hover:underline transition-colors text-sm"
                  onClick={scrollToTop}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;